import posthog from "posthog-js";
import { RENDERER } from "@/constants/QueryKeys";
import { getAccountData, isAdmin } from "@/hooks/query/useAccount";
import { getSiteId } from "@/hooks/query/useSite";
import { subscribeToQuery } from "@/utils/queryCacheBridge";
import { queryClient } from "@/utils/QueryUtils";

const DEV_USER = "localbox@showit.com";

/**
 * Whether the session is internal: the local dev account, or a Showit admin.
 * @returns {boolean}
 */
export const isInternalSession = () => getAccountData()?.email === DEV_USER || isAdmin();

/**
 * Whether the flag is already known to be on, without waiting for PostHog to load flags.
 * @param {string} flag
 * @returns {boolean}
 */
export const isFeatureFlagEnabled = (flag) =>
  isInternalSession() || posthog.isFeatureEnabled(flag) === true;

/**
 * Gates a feature to v2 designs (past the renderer cutoff), for code outside React. Without a flag
 * every user on a v2 design qualifies; with a flag the user must also be internal or have it.
 * Components use `useV2Feature`, which applies the same rule.
 * @param {string} [flag] - PostHog flag to check.
 * @returns {boolean}
 */
export const isV2FeatureEnabled = (flag) => {
  const renderer = queryClient.getQueryData([RENDERER, getSiteId()]);
  const isV2Design = renderer?.renderer != null && !renderer.canToggle;
  return isV2Design && (!flag || isFeatureFlagEnabled(flag));
};

/**
 * Runs the callback once `isV2FeatureEnabled(flag)` holds, checking again whenever the site's
 * renderer or PostHog's flags load.
 * @param {string} [flag] - PostHog flag to check.
 * @param {() => void} callback
 * @returns {() => void} Stops waiting.
 */
export const whenV2FeatureEnabled = (flag, callback) => {
  let done = false;
  let unsubscribeRenderer = () => {};
  let unsubscribeFlags = () => {};
  const stop = () => {
    done = true;
    unsubscribeRenderer();
    unsubscribeFlags();
  };
  const check = () => {
    if (done || !isV2FeatureEnabled(flag)) return;
    stop();
    callback();
  };

  check();
  if (done) return stop;

  unsubscribeRenderer = subscribeToQuery([RENDERER, getSiteId()], check);
  // PostHog calls back straight away when flags are already loaded, so `check` may finish first.
  unsubscribeFlags = posthog.onFeatureFlags(check) ?? (() => {});
  if (done) stop();

  return stop;
};
